/**
 * PlaceSearchField — keyword place/address autocomplete for a free-text destination.
 *
 * Mobile twin: `svdy-mobile/src/components/PlaceSearchField.tsx`, which has shipped
 * this on `business_trip.destination` and `off_site_work.location` since 2026-08-07
 * while THIS client rendered the same two fields as a plain text box. Same form, same
 * field, two capability levels (owner, 2026-08-24).
 *
 * Shared behaviour contract with the mobile twin — update in lockstep (svdy-ui §13):
 *
 *   | concern            | both clients                                              |
 *   |--------------------|-----------------------------------------------------------|
 *   | min query          | 2 characters                                              |
 *   | debounce           | 280 ms                                                    |
 *   | stored value       | a plain STRING (the prediction's text, or free typing)    |
 *   | coordinates        | none — no Place Details call, the request needs a name     |
 *   | no key / failure   | behaves exactly like a plain text input, never a gate      |
 *
 * The two clients necessarily call DIFFERENT Google APIs — React Native has no Maps JS
 * SDK, so mobile uses the Places (New) REST endpoint while this reuses the JS SDK's
 * `AutocompleteService` through `useSvdyGoogleMaps`. That singleton loader is the part
 * worth reusing: it throws if any caller changes one loader option, so a second
 * ad-hoc loader here would break every existing map surface.
 *
 * Why not `LocationPickerDialog`: that is the ADMIN address picker — map, geocode,
 * timezone resolution, structured street/city/country output — for a row that stores
 * coordinates. A worker naming where they are going needs one line of text, and a
 * modal map for it would be heavier than the task (svdy-rules §16, no over-design).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSvdyGoogleMaps } from './googleMaps';

/** Matches the mobile twin, which matches the ColleaguePicker debounce family. */
const DEBOUNCE_MS = 280;
const MIN_QUERY = 2;

export interface PlaceSearchFieldProps {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const PlaceSearchField: React.FC<PlaceSearchFieldProps> = ({
  value, onChange, label, required, placeholder, disabled,
}) => {
  const { t } = useTranslation();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useSvdyGoogleMaps();

  const service = useRef<google.maps.places.AutocompleteService | null>(null);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Autocomplete is an ENHANCEMENT. Without a key, before the SDK loads, or after a
  // load failure the field must still accept typing — this is the same graceful
  // degradation the mobile twin documents, and the reason a China-mainland tenant
  // (Google blocked) never sees a broken control.
  const enhanced = Boolean(apiKey) && isLoaded && !loadError;

  useEffect(() => {
    if (!enhanced || service.current) return;
    service.current = new google.maps.places.AutocompleteService();
    sessionToken.current = new google.maps.places.AutocompleteSessionToken();
  }, [enhanced]);

  const fetchPredictions = useCallback((query: string) => {
    if (!service.current || query.trim().length < MIN_QUERY) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    service.current.getPlacePredictions(
      { input: query, sessionToken: sessionToken.current ?? undefined },
      (predictions, status) => {
        setLoading(false);
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          setOptions([]);
          return;
        }
        setOptions(predictions.map((prediction) => prediction.description));
      },
    );
  }, []);

  useEffect(() => {
    if (!enhanced) return undefined;
    const timer = setTimeout(() => fetchPredictions(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enhanced, inputValue, fetchPredictions]);

  // Keep the visible text in step when the parent resets the form.
  useEffect(() => { setInputValue(value ?? ''); }, [value]);

  const resolvedPlaceholder = useMemo(
    () => placeholder ?? t('common:location.placeSearchPlaceholder'),
    [placeholder, t],
  );

  if (!enhanced) {
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        required={required}
        disabled={disabled}
        placeholder={resolvedPlaceholder}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <Autocomplete<string, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      // The server decides what matches; a client substring filter would hide
      // predictions Google already ranked (same reason as ColleaguePicker).
      filterOptions={(option) => option}
      inputValue={inputValue}
      onInputChange={(_, next) => {
        setInputValue(next);
        // `freeSolo` means whatever is typed IS the value — the field must work for a
        // destination Google has never heard of.
        onChange(next);
      }}
      onChange={(_, picked) => {
        const next = typeof picked === 'string' ? picked : '';
        setInputValue(next);
        onChange(next);
      }}
      loading={loading}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={resolvedPlaceholder}
          // A PERSISTENT hint, not just the placeholder: a placeholder disappears the
          // moment you type, which is exactly when you would want to know suggestions are
          // coming (owner, 2026-08-25). Rendered ONLY on the `enhanced` path — without a
          // Maps key there are no suggestions, and promising them would be a lie.
          helperText={t('common:location.placeSearchHint')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PlaceSearchField;
